import {noop} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import {
  DataModificationsHeader,
  decodeReasonCodeAndCommentHeader,
  ReasonCodeAndCommentType,
} from 'audit/DataModifications';
import ReasonCodeAndCommentModal, {ReasonCodeAndCommentModalProps} from 'audit/ReasonCodeAndCommentModal';
import BenchlingConfig from 'BenchlingConfig';
import {getUniqueErrorMessages} from 'notebook/tables/errors/ErrorUtil';
import {EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import legacyRunModal from 'ui-library/legacyRunModal';
import {OptionData} from 'ui-library/menus/MenuConstants';

const TABLE_VALIDATION_BYPASS_MODAL_KEY_PREFIX = 'TABLE_VALIDATION_BYPASS_MODAL_KEY';

export interface TableValidationBypassModalProps {
  onRequestSubmit: (reasonCodeAndCommentHeader: DataModificationsHeader) => Promise<void> | void;
  errorRows: Array<Array<EvaluatedCellError>>;
  onRequestClose: () => void;
  modalKey: string;
}

export type CallWithTableValidationBypassModalArgs = Omit<
  TableValidationBypassModalProps,
  'onRequestClose' | 'modalKey'
>;

export async function callWithTableValidationBypassModal({
  onRequestSubmit,
  errorRows,
}: CallWithTableValidationBypassModalArgs): Promise<DataModificationsHeader> {
  return (await legacyRunModal<TableValidationBypassModalProps>(TableValidationBypassModal, {
    onRequestSubmit,
    onRequestClose: noop,
    modalKey: _.uniqueId(TABLE_VALIDATION_BYPASS_MODAL_KEY_PREFIX),
    errorRows,
  })) as unknown as DataModificationsHeader;
}

export default function TableValidationBypassModal(props: TableValidationBypassModalProps): JSX.Element {
  const _validationErrorSummaryBanner = (errorRows: Array<Array<EvaluatedCellError>>): JSX.Element => {
    const uniqueErrors = getUniqueErrorMessages(errorRows);

    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text={`${uniqueErrors.length} error${uniqueErrors.length > 1 ? 's' : ''} will be bypassed`}
        supportingInfo={uniqueErrors.join('\n')}
        dataTestAttrs={{component: 'TableValidationBypassModal', element: 'summaryBanner'}}
      />
    );
  };

  const _getReasonCodes = (): Array<OptionData> => {
    const reasonCodes = BenchlingConfig.AUDITING_PROCEDURE_VALIDATION_OVERRIDE_REASONS.map(
      (reason: string) => ({
        name: reason,
        value: reason,
      })
    );
    assert(reasonCodes.length > 0, 'The AUDITING_PROCEDURE_VALIDATION_OVERRIDE_REASONS config is empty');
    return reasonCodes;
  };

  const _logAnalytics = (encryptedHeader: DataModificationsHeader): void => {
    const reasonCodeAndCommentHeader = decodeReasonCodeAndCommentHeader(encryptedHeader);
    const reasonCode =
      reasonCodeAndCommentHeader[ReasonCodeAndCommentType.BYPASS_VALIDATION_ERROR]?.reasonCode;

    if (reasonCode) {
      analytics.trackEvent(
        {
          module: ANALYTICS_MODULE.FORMS,
          action: 'submitted_bypassing_validation',
          actionCategory: ACTION_CATEGORY.WRITE,
        },
        {
          reasonCode,
        }
      );
    }
  };

  const onRequestSubmit = async (reasonCodeAndCommentHeader: DataModificationsHeader): Promise<void> => {
    await props.onRequestSubmit(reasonCodeAndCommentHeader);
    _logAnalytics(reasonCodeAndCommentHeader);
  };

  const modalProps: ReasonCodeAndCommentModalProps = {
    changeSummary: _validationErrorSummaryBanner(props.errorRows),
    onRequestSubmit,
    onRequestClose: props.onRequestClose,
    modalKey: props.modalKey,
    modalKeyPrefix: TABLE_VALIDATION_BYPASS_MODAL_KEY_PREFIX,
    modalTitle: 'Submit and bypass validation',
    reasonCodes: _getReasonCodes,
    isCommentOptional: true,
    dataTestAttrs: {component: 'TableValidationBypassModal'},
    reasonCodeAndCommentType: ReasonCodeAndCommentType.BYPASS_VALIDATION_ERROR,
  };

  return <ReasonCodeAndCommentModal {...modalProps} />;
}
